import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { MapPin, ArrowRight } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';
import { resolveImage } from '../lib/api';

const EventsPage = () => {
  const { events, settings } = useSiteData();
  const { t } = useLang();
  const header = settings?.page_headers?.events || {};
  const items = (events || []).slice().sort((a, b) => a.order - b.order);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Government Events"
        description="Upcoming forums, workshops, consultations and official events hosted by the South East State of Somalia government."
        keywords="Somalia events, government workshops, Marka Cadeey events, South East State consultations"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: items.slice(0, 10).map((e, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'Event',
              name: e.title,
              startDate: `${e.year}-${e.month}-${e.day}`,
              location: e.location ? { '@type': 'Place', name: e.location } : undefined,
              description: e.description,
              image: e.image_url ? resolveImage(e.image_url) : undefined,
            },
          })),
        }}
      />
      <PageHeader
        title={header.title || 'Government Events'}
        subtitle={header.subtitle}
        crumbs={[{ label: 'Events' }]}
      />
      <section className="bg-[#FAF6EC] py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          {items.length === 0 ? (
            <div className="rounded-lg border border-[#E6DDC8] bg-white p-12 text-center text-[#0B2545]/60">No events scheduled.</div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {items.map((e) => (
                <div key={e.id} className="group flex flex-col overflow-hidden rounded-lg border border-[#E6DDC8] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative h-[200px] w-full overflow-hidden">
                    {e.image_url && (
                      <img src={resolveImage(e.image_url)} alt={e.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                    <div className="absolute left-4 top-4 flex flex-col items-center rounded-md bg-[#0B2545] px-3 py-2 text-white shadow-lg">
                      <div className="font-serif text-[22px] font-bold leading-none">{e.day}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-[#C9A86A]">{e.month}</div>
                      <div className="text-[10px] text-white/70">{e.year}</div>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="line-clamp-2 font-serif text-[18px] font-semibold leading-snug text-[#0B2545]">{e.title}</h3>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-[#0B2545]/65">
                      <MapPin size={13} className="text-[#C9A86A]" /> {e.location}
                    </div>
                    <p className="mt-4 line-clamp-3 text-[13.5px] leading-relaxed text-[#0B2545]/70">{e.description}</p>
                    <div className="mt-auto flex items-center justify-end border-t border-[#E6DDC8] pt-4">
                      <Link
                        to={`/events/${e.id}`}
                        data-testid="event-read-more"
                        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#C9A86A] transition-colors hover:text-[#0B2545]"
                      >
                        {t('View Details')}
                        <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default EventsPage;
